import { CompanyParametersQuery, CurrencyCollectionQuery, VatRateForPurchaseQuery, VatTableItemGrossRecalculation, VatTableItemNetRecalculation, propertyChangedJoinPointFilter } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, ModelBase, PropertyChangedJoinPoint, Weaver } from '@skylab-shared/core';
import { PurchaseVatTableItemModel, VatTableItem, VatTableItemModel } from '@skylab-shared/dataModel';
import { PurchaseIncorrectVatValueWarning } from '../violations/purchase-incorrect-vat-value-warning';

export interface PurchaseInvoiceVatTableValidationsBoPartModel extends ModelBase {
    automaticVatCalculation: boolean;
}

export class PurchaseInvoiceVatTableValidationsBoPart extends BusinessObjectPart<PurchaseInvoiceVatTableValidationsBoPartModel> {
    private readonly vatRateNetCalculator: VatTableItemNetRecalculation;
    private readonly vatRateGrossCalculator: VatTableItemGrossRecalculation;

    public constructor(weaver: Weaver, private readonly isDocumentAdjusted: () => boolean) {
        super(weaver);
        this.vatRateNetCalculator = new VatTableItemNetRecalculation(); 
        this.vatRateGrossCalculator = new VatTableItemGrossRecalculation();
    }

    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseInvoiceVatTableValidationsBoPartModel>): Promise<void> {
        await super.initializeAsync(context);
        await Promise.all([
            this.bo.localContext.preload(CompanyParametersQuery),
            this.bo.localContext.preload(CurrencyCollectionQuery),
            this.bo.localContext.preload(VatRateForPurchaseQuery)
        ]);

        this.registerIncorrectVatValueRule();
        this.registerReadonlyRules();
    }

    private registerReadonlyRules(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['automaticVatCalculation'])(
            () => {
                this.engine.readonly.queueValidation([PurchaseVatTableItemModel, 'netValue']);
                this.engine.readonly.queueValidation([PurchaseVatTableItemModel, 'vatValue']);
                this.engine.readonly.queueValidation([PurchaseVatTableItemModel, 'grossValue']);
            }
        )));

        this.engine.readonly.setReadonlyOnSubtree(VatTableItemModel, () => this.bo.model.automaticVatCalculation || this.isDocumentAdjusted());
    }

    private registerIncorrectVatValueRule(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['automaticVatCalculation'])(
            () => this.engine.validation.queueValidation([VatTableItemModel, 'vatValue'])
        )));

        this.engine.validation.addRule(
            [[VatTableItemModel, 'vatValue'], [VatTableItemModel, 'netValue'], [VatTableItemModel, 'grossValue']],
            [[VatTableItemModel, 'vatValue']],
            ctx => {
                if (this.bo.model.automaticVatCalculation) return true;

                const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
                const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency });
                const { rate: vatRate } = this.bo.localContext.firstPreloaded(VatRateForPurchaseQuery, { id: ctx.source.vatRateId });
                const convertedItem: VatTableItem = ({grossValue: ctx.source.grossValue, netValue: ctx.source.netValue, vatRate, vatRateId: ctx.source.vatRateId, vatValue: ctx.source.vatValue});

                const correctItemFromNet = this.vatRateNetCalculator.recalculate(convertedItem, precision, 'net');
                const correctItemFromGross = this.vatRateGrossCalculator.recalculate(convertedItem, precision, 'gross');
                const isCorrectFromNet = correctItemFromNet.vatValue === ctx.source.vatValue && correctItemFromNet.netValue === ctx.source.netValue && correctItemFromNet.grossValue === ctx.source.grossValue;
                const isCorrectFromGross = correctItemFromGross.vatValue === ctx.source.vatValue && correctItemFromGross.netValue === ctx.source.netValue && correctItemFromGross.grossValue === ctx.source.grossValue;
                return isCorrectFromGross || isCorrectFromNet;
            },
            PurchaseIncorrectVatValueWarning
        );
    }
}
